import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { toast } from "sonner";
import axios from "axios";
import { buildWhatsappUrl, WHATSAPP_DISPLAY } from "../constants/site";
import { useSiteSettings } from "../hooks/useSiteSettings";
import { QUOTE } from "../constants/testIds";
import { MessageCircle, Send, Loader2 } from "lucide-react";

const BASE_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BASE_URL}/api`;

const formatWhatsappMessage = (data) => {
    const lines = [
        "*Nouvelle demande de devis — Atelier AX*",
        "",
        `• Nom : ${data.full_name}`,
        data.clinic_name ? `• Boutique / Magasin : ${data.clinic_name}` : null,
        `• Téléphone : ${data.phone}`,
        data.city ? `• Ville : ${data.city}` : null,
        "",
        data.product ? `*Produit ciblé :* ${data.product}` : null,
        data.message ? `*Message :*\n${data.message}` : null,
    ].filter(Boolean);
    return lines.join("\n");
};

const QuoteDialog = ({ open, onOpenChange, defaultProduct = "" }) => {
    const { data: settings } = useSiteSettings();
    const whatsappNumber = settings?.whatsapp_number || WHATSAPP_DISPLAY.replace(/\D/g, "");
    const whatsappDisplay = settings?.whatsapp_display || WHATSAPP_DISPLAY;
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        full_name: "",
        clinic_name: "",
        phone: "",
        city: "",
        product: defaultProduct,
        message: "",
    });

    React.useEffect(() => {
        if (defaultProduct) {
            setForm((f) => ({ ...f, product: defaultProduct }));
        }
    }, [defaultProduct]);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const handleSend = () => {
        if (!form.full_name || !form.phone) {
            toast.error("Renseignez votre nom et votre numéro de téléphone.");
            return;
        }
        setSubmitting(true);

        const url = buildWhatsappUrl(formatWhatsappMessage(form), whatsappNumber);
        // Ouvrir WhatsApp immédiatement pendant que le geste utilisateur est actif,
        // sinon les bloqueurs de pop-up empêchent window.open après un appel async.
        const win = window.open(url, "_blank");
        if (!win) {
            window.location.href = url;
        }

        axios
            .post(`${API}/quote-requests`, {
                full_name: form.full_name,
                clinic_name: form.clinic_name || null,
                email: null,
                phone: form.phone,
                city: form.city || null,
                message: [form.product ? `Produit : ${form.product}` : "", form.message]
                    .filter(Boolean)
                    .join("\n"),
                items: form.product
                    ? [{ product_id: form.product, name: form.product, quantity: 1 }]
                    : [],
            })
            .then(() => {
                toast.success("Devis transmis. WhatsApp ouvert.");
            })
            .catch((err) => {
                console.error(err);
                toast.message("Devis ouvert dans WhatsApp.");
            })
            .finally(() => {
                setSubmitting(false);
                onOpenChange(false);
            });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid={QUOTE.dialog}
                className="max-w-xl bg-white border border-black/5 shadow-2xl rounded-2xl max-h-[90vh] overflow-y-auto"
            >
                <DialogHeader>
                    <div className="dp-eyebrow mb-2">Devis personnalisé</div>
                    <DialogTitle className="font-display text-2xl tracking-tight">
                        Recevez votre devis sur WhatsApp
                    </DialogTitle>
                    <DialogDescription className="text-[15px] text-neutral-500">
                        Nous formulons votre demande et l'envoyons à notre équipe via WhatsApp
                        ({whatsappDisplay}). Réponse sous quelques minutes ouvrées.
                    </DialogDescription>
                </DialogHeader>

                <div className="grid grid-cols-2 gap-4 mt-2">
                    <div className="col-span-2 sm:col-span-1 space-y-1.5">
                        <Label className="text-xs font-medium">Nom complet *</Label>
                        <Input
                            data-testid={QUOTE.inputName}
                            value={form.full_name}
                            onChange={update("full_name")}
                            placeholder="Karim Alaoui"
                        />
                    </div>
                    <div className="col-span-2 sm:col-span-1 space-y-1.5">
                        <Label className="text-xs font-medium">Boutique / Magasin</Label>
                        <Input
                            data-testid={QUOTE.inputClinic}
                            value={form.clinic_name}
                            onChange={update("clinic_name")}
                            placeholder="Atelier AX Rabat"
                        />
                    </div>
                    <div className="col-span-2 sm:col-span-1 space-y-1.5">
                        <Label className="text-xs font-medium">Téléphone *</Label>
                        <Input
                            data-testid={QUOTE.inputPhone}
                            value={form.phone}
                            onChange={update("phone")}
                            placeholder="+212 6 12 34 56 78"
                        />
                    </div>
                    <div className="col-span-2 sm:col-span-1 space-y-1.5">
                        <Label className="text-xs font-medium">Ville</Label>
                        <Input
                            data-testid={QUOTE.inputCity}
                            value={form.city}
                            onChange={update("city")}
                            placeholder="Votre ville au Maroc…"
                        />
                    </div>
                    <div className="col-span-2 space-y-1.5">
                        <Label className="text-xs font-medium">Produit ciblé</Label>
                        <Input
                            data-testid={QUOTE.inputProduct}
                            value={form.product}
                            onChange={update("product")}
                            placeholder="Coque iPhone 15 / Chargeur 65 W…"
                        />
                    </div>
                    <div className="col-span-2 space-y-1.5">
                        <Label className="text-xs font-medium">Message</Label>
                        <Textarea
                            data-testid={QUOTE.inputMessage}
                            value={form.message}
                            onChange={update("message")}
                            rows={4}
                            placeholder="Modèle de téléphone, quantités, couleur, délais…"
                        />
                    </div>
                </div>

                <DialogFooter className="mt-2 gap-2 sm:gap-2">
                    <Button
                        variant="ghost"
                        onClick={() => onOpenChange(false)}
                        className="rounded-full"
                    >
                        Annuler
                    </Button>
                    <Button
                        data-testid={QUOTE.sendWhatsapp}
                        onClick={handleSend}
                        disabled={submitting}
                        className="rounded-full bg-[#1C1C1E] hover:bg-black text-white"
                    >
                        {submitting ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                            <MessageCircle className="h-4 w-4 mr-2" />
                        )}
                        Envoyer via WhatsApp
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default QuoteDialog;
